/**
 * Files chosen but not yet uploaded: pick, drop, or paste; review; remove.
 *
 * Refusals happen here first so an engineer is told "too big" before the bytes
 * leave the machine -- the server checks the same rules again from `/tickets/limits`,
 * because a client-side check is a courtesy, not a control.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type DragEvent } from 'react'
import { FileText, Paperclip, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TicketLimits } from '@/api/support'
import { formatBytes } from '@/lib/format'
import { cn } from '@/lib/cn'

const FALLBACK_LIMITS: TicketLimits = {
  max_attachment_bytes: 2 * 1024 * 1024,
  allowed_media_types: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'],
  max_attachments_per_message: 10,
  max_attachments_per_ticket: 50,
}

const EXTENSION_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  pdf: 'application/pdf',
}

/** The browser's idea of the type, falling back to the extension for pasted blobs. */
function guessType(file: File): string {
  if (file.type) return file.type
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  return EXTENSION_TYPES[extension] ?? ''
}

export interface AttachmentDraft {
  files: File[]
  errors: string[]
  add: (incoming: Iterable<File>) => void
  remove: (file: File) => void
  clear: () => void
  limits: TicketLimits
}

export function useAttachmentDraft(limits: TicketLimits | undefined): AttachmentDraft {
  const { t } = useTranslation()
  const effective = limits ?? FALLBACK_LIMITS
  const [files, setFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])

  const add = useCallback(
    (incoming: Iterable<File>) => {
      const rejected: string[] = []
      const accepted: File[] = []
      for (const file of incoming) {
        const type = guessType(file)
        if (!effective.allowed_media_types.includes(type)) {
          rejected.push(t('support.attachments.rejected_type', { name: file.name }))
        } else if (file.size > effective.max_attachment_bytes) {
          rejected.push(
            t('support.attachments.rejected_size', {
              name: file.name,
              max: formatBytes(effective.max_attachment_bytes),
            }),
          )
        } else if (file.size === 0) {
          rejected.push(t('support.attachments.rejected_empty', { name: file.name }))
        } else {
          accepted.push(file)
        }
      }
      setFiles((current) => {
        const room = effective.max_attachments_per_message - current.length
        if (accepted.length > room) {
          rejected.push(
            t('support.attachments.rejected_count', { max: effective.max_attachments_per_message }),
          )
        }
        return [...current, ...accepted.slice(0, Math.max(0, room))]
      })
      setErrors(rejected)
    },
    [effective, t],
  )

  const remove = useCallback((file: File) => {
    setFiles((current) => current.filter((f) => f !== file))
  }, [])

  const clear = useCallback(() => {
    setFiles([])
    setErrors([])
  }, [])

  return { files, errors, add, remove, clear, limits: effective }
}

export function AttachmentPicker({
  draft,
  disabled,
  compact,
}: {
  draft: AttachmentDraft
  disabled?: boolean
  /** Reply boxes get a one-line control; the new-request page gets a drop zone. */
  compact?: boolean
}) {
  const { t } = useTranslation()
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const accept = useMemo(
    () => draft.limits.allowed_media_types.join(','),
    [draft.limits.allowed_media_types],
  )

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    if (disabled) return
    draft.add(Array.from(event.dataTransfer.files))
  }

  return (
    <div
      className={cn(
        'space-y-2 rounded-md border border-dashed transition-colors',
        dragging ? 'border-accent bg-accent-subtle' : 'border-border',
        compact ? 'p-2' : 'p-3',
      )}
      onDragOver={(event) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple
          accept={accept}
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            draft.add(Array.from(event.target.files ?? []))
            event.target.value = ''
          }}
        />
        <label
          htmlFor={inputId}
          className={cn(
            'inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm text-text hover:bg-surface-raised',
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          <Paperclip size={14} aria-hidden />
          {t('support.attachments.add')}
        </label>
        <span className="text-xs text-text-muted">
          {t('support.attachments.hint', { max: formatBytes(draft.limits.max_attachment_bytes) })}
        </span>
      </div>

      {draft.files.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label={t('support.attachments.pending')}>
          {draft.files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="relative flex max-w-full items-center gap-2 rounded-md border border-border bg-surface p-1 pe-2 text-xs"
            >
              <LocalThumbnail file={file} />
              <span className="min-w-0">
                <span className="block max-w-[10rem] truncate font-medium">{file.name}</span>
                <span className="text-text-muted">{formatBytes(file.size)}</span>
              </span>
              <button
                type="button"
                className="ms-1 flex h-7 w-7 items-center justify-center rounded text-text-muted hover:text-text"
                aria-label={t('support.attachments.remove', { name: file.name })}
                onClick={() => draft.remove(file)}
                disabled={disabled}
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {draft.errors.length > 0 && (
        <ul role="alert" className="space-y-0.5 text-xs text-[var(--status-fail)]">
          {draft.errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function LocalThumbnail({ file }: { file: File }) {
  const isImage = guessType(file).startsWith('image/')
  // The URL is derived from the file, not state; the effect only revokes it.
  const url = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage])

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])

  return (
    <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-surface-raised">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <FileText size={20} aria-hidden className="text-text-muted" />
      )}
    </span>
  )
}

/** Files carried by a paste event, if any. Text pastes return an empty list. */
export function pastedFiles(event: { clipboardData: DataTransfer | null }): File[] {
  const data = event.clipboardData
  if (!data) return []
  const files: File[] = []
  for (const item of Array.from(data.items)) {
    if (item.kind !== 'file') continue
    const file = item.getAsFile()
    if (!file) continue
    // A pasted screenshot arrives as "image.png"; give it a name that says when.
    if (file.name === 'image.png' || file.name === '') {
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
      const extension = file.type.split('/')[1] || 'png'
      files.push(new File([file], `pasted-${stamp}.${extension}`, { type: file.type }))
    } else {
      files.push(file)
    }
  }
  return files
}
